import { ErrorFlow } from 'olympe';
import { Autocomplete, TextField } from '@mui/material';
import React from 'react';

// prettier-ignore
export const MasterDataAutoComplete = React.forwardRef(({context, logger, getOptions, path, filter, value, getOptionLabel, isOptionEqualToValue, onChange, textFieldLabel, ...other}, ref) => {

    const [loading, setLoading] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState([]);
    // eslint-disable-next-line no-unused-vars
    const [total, setTotal] = React.useState(0);
    const [userInput, setUserInput] = React.useState('');
    const debouncedTextInput = useDebounce(userInput, 500);
    const [queriedText, setQueriedText] = React.useState('');

    const renderInput = (params) => (
        <TextField {...params} label={textFieldLabel} />
    );

    React.useEffect(() => {
        // avoid performing query with empty search
        if (!debouncedTextInput) {
            return;
        }
        // avoid performing several queries simultaneously
        if (loading) {
            return;
        }
        // avoid performing a query when the autocomplete is closed
        if (open === false) {
            // return;
        }
        const searchInput = debouncedTextInput?.trim();
        // avoid performing a query of with the same input as the last query
        if (queriedText === searchInput && options.length > 0) {
            return;
        }

        setLoading(true);
        setOptions([]);
        (async () => {
            try {

                const [
                    controlFlow,
                    pathInput,
                    filtersInput,
                ] = getOptions.getInputs();

                const [
                    successOutput,
                    resultsOutput,
                    totalOutput,
                    errorOutput,
                ] = getOptions.getOutputs();
    
                const getOptionsRunner = context.runner(getOptions)
                    .set(pathInput, path)
                    .set(filtersInput, filter(searchInput));

                getOptionsRunner.trigger(controlFlow);    

                const [results, total] = await Promise.any([
                    getOptionsRunner.waitFor(successOutput),
                    getOptionsRunner.waitFor(errorOutput),
                ]).then((outputFlow) => {
                    if (outputFlow instanceof ErrorFlow) {
                        throw new Error(outputFlow.getMessage());
                    } else {
                        return [getOptionsRunner.get(resultsOutput), getOptionsRunner.get(totalOutput)];
                    }
                }).finally(() => getOptionsRunner.destroy());

                setOptions(results ?? []);
                setTotal(total);
                setQueriedText(searchInput);
            } catch (err) {
                logger.error(`Failed to get autocomplete options: ${err}`);
            }
            setLoading(false);
        })();
    }, [open, debouncedTextInput]);

    const handleChange = (value) => {
        onChange(value);
    };

    return (
        <Autocomplete
            freeSolo={true}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            forcePopupIcon={true}
            clearOnBlur={false}
            loading={loading}
            options={options}
            getOptionLabel={getOptionLabel}
            renderInput={renderInput}
            isOptionEqualToValue={isOptionEqualToValue}
            onInputChange={(event, newInputValue) => {
                setUserInput(newInputValue);
            }}      
            onChange={(event, value) => handleChange(value)}
            inputValue={userInput ?? ''}
            value={value ?? ''}
            ref={ref}
            {...other}
        ></Autocomplete>
    )
});

/**
 * React hook to update a value based on a delay (debounced)
 * @param {string} value
 * @param {number} delay - in milliseconds
 * @returns {string}
 */
export const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = React.useState(value);
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);
    return debouncedValue;
};
